import { useEffect, useMemo, useState } from "react";

/**
 * Debounces a value. Used for the search box on tables whose filtering hits the server, so a
 * request is not fired on every keystroke.
 */
export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface ClientTable<T> {
  /** The rows for the current page, after filtering. */
  rows: T[];
  /** Rows matching the filter across all pages — the honest "of Z". */
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  search: string;
  setSearch: (value: string) => void;
}

/**
 * Search + pagination over a complete, already-fetched list.
 *
 * This is the correct model for `/templates`, `/devices` and `/api-keys`: those endpoints take no
 * query parameters at all and return the entire array in one response, so the client genuinely
 * holds every row. Filtering and paging here therefore describe the real dataset — "Showing 1–10
 * of 43" is a true statement, not a client-side illusion over a partial page.
 *
 * It would be the wrong model for `/messages` and `/contacts`, which are cursor-paginated and
 * only ever hand over one page; those use the server's own filtering instead.
 */
export function useClientTable<T>(
  data: T[] | undefined,
  searchableFields: (row: T) => Array<string | null | undefined>,
  initialPageSize = 10,
): ClientTable<T> {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const all = data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((row) =>
      searchableFields(row).some((field) => field?.toLowerCase().includes(needle)),
    );
    // searchableFields is defined inline at every call site and so is a new function each render;
    // depending on it would recompute constantly. The fields it reads are derived from `data`,
    // which is in the dependency list, so this stays correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Deleting the last row of the final page, or narrowing a search, can leave `page` past the
  // end — which would render an empty table with rows that do exist. Clamp instead.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const current = Math.min(page, pageCount);
  const rows = useMemo(
    () => filtered.slice((current - 1) * pageSize, current * pageSize),
    [filtered, current, pageSize],
  );

  return {
    rows,
    total,
    page: current,
    pageSize,
    setPage,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1); // staying on page 7 while switching to 100-per-page would land past the end
    },
    search,
    setSearch: (value: string) => {
      setSearch(value);
      setPage(1); // a new filter always starts at the beginning of its own result set
    },
  };
}
